import { useEffect, useMemo, useState } from "react";
import { Phone, Mail, MapPin, Send, Search, Navigation } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { MATHA } from "@/lib/constants";
import { useLang } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import MandalaDivider from "@/components/MandalaDivider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { branchLocations } from "@/mockData";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const { t, lv } = useLang();
  const c = t.contact;

  useEffect(() => { setBranches(branchLocations); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return branches;
    const s = q.toLowerCase();
    return branches.filter(b =>
      [b.name, b.address, b.city, b.state].join(" ").toLowerCase().includes(s)
    );
  }, [q, branches]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error(c.errFill);
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/contact", form);
      toast.success(c.okSent);
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(c.errSent);
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = "text-[12px] uppercase tracking-[0.18em] text-[#8a6a3a]";

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow={c.eyebrow} title={c.title} sub={c.sub} />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-5">
            <div className="card-soft p-6">
              <div className="w-11 h-11 rounded-full bg-[#F4EBDB] text-[#7B1818] flex items-center justify-center"><MapPin size={18} /></div>
              <div className="eyebrow mt-4">{c.addressT}</div>
              <p className="mt-2 text-[15px] text-[#2A2118] font-serif-display leading-relaxed">
                {t.matha.address}
              </p>
            </div>

            <div className="card-soft p-6">
              <div className="w-11 h-11 rounded-full bg-[#F4EBDB] text-[#7B1818] flex items-center justify-center"><Phone size={18} /></div>
              <div className="eyebrow mt-4">{c.phoneT}</div>
              <a data-testid="contact-phone" href={`tel:${MATHA.phone.replace(/\s/g, "")}`} className="mt-2 block text-[15px] text-[#2A2118] hover:text-[#7B1818]">
                {MATHA.phone}
              </a>
            </div>

            <div className="card-soft p-6">
              <div className="w-11 h-11 rounded-full bg-[#F4EBDB] text-[#7B1818] flex items-center justify-center"><Mail size={18} /></div>
              <div className="eyebrow mt-4">{c.emailT}</div>
              <a data-testid="contact-email" href={`mailto:${MATHA.email}`} className="mt-2 block text-[15px] text-[#2A2118] hover:text-[#7B1818] break-all">
                {MATHA.email}
              </a>
            </div>

            <div className="card-soft overflow-hidden aspect-video">
              <iframe
                title="Matha Location"
                src="https://www.google.com/maps?q=Bhimanakatte%20Thirthahalli&output=embed"
                width="100%" height="100%" style={{ border: 0 }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="card-soft p-8 lg:p-10">
              <div className="eyebrow">{c.formEyebrow}</div>
              <h2 className="mt-3 font-display text-3xl lg:text-4xl text-[#2A2118] leading-tight">
                {c.formTitle}
              </h2>
              <p className="mt-3 text-[14.5px] text-[#5C4D42]">{c.formSub}</p>

              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className={labelCls}>{c.name}</Label>
                  <Input data-testid="contact-name-input" id="name" value={form.name} onChange={set("name")} className="mt-2 h-12 border-[#E5DAC7]" required />
                </div>
                <div>
                  <Label htmlFor="phone" className={labelCls}>{c.phone}</Label>
                  <Input data-testid="contact-phone-input" id="phone" value={form.phone} onChange={set("phone")} className="mt-2 h-12 border-[#E5DAC7]" required />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="email" className={labelCls}>{c.email}</Label>
                  <Input data-testid="contact-email-input" id="email" type="email" value={form.email} onChange={set("email")} className="mt-2 h-12 border-[#E5DAC7]" required />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message" className={labelCls}>{c.message}</Label>
                  <Textarea data-testid="contact-message-input" id="message" rows={5} value={form.message} onChange={set("message")} className="mt-2 border-[#E5DAC7]" required />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="btn-primary mt-8 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? c.sending : <>{c.send} <Send size={16} /></>}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section data-testid="branches-section" className="section-pad pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center">
            <div className="eyebrow">{c.branchesEyebrow}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#2A2118] leading-[1.1]">
              {c.branchesTitle}
            </h2>
            <p className="mt-3 text-[14px] text-[#5C4D42] max-w-xl mx-auto">
              {c.branchesSub}
            </p>
            <div className="mt-5 flex justify-center"><MandalaDivider /></div>
          </div>

          <div className="mt-10 max-w-md mx-auto relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a6a3a]" />
            <Input
              data-testid="branches-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={c.search}
              className="pl-11 h-12 rounded-full border-[#E5DAC7] bg-white"
            />
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((b, i) => (
              <article key={b.id} data-testid={`branch-card-${i}`} className="card-soft reveal p-6">
                <h3 className="font-display text-xl text-[#2A2118] leading-snug">{lv(b, "name")}</h3>
                <p className="mt-3 text-[13.5px] text-[#5C4D42] flex gap-2">
                  <MapPin size={14} className="mt-0.5 text-[#7B1818] shrink-0" />
                  <span>{b.address}, {b.city}, {b.state} {b.pincode || ""}</span>
                </p>
                {b.phone && (
                  <p className="mt-1.5 text-[13.5px] text-[#5C4D42] flex gap-2">
                    <Phone size={14} className="mt-0.5 text-[#7B1818] shrink-0" />
                    <a href={`tel:${b.phone.replace(/\s/g, "")}`} className="hover:text-[#7B1818]">{b.phone}</a>
                  </p>
                )}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.address + " " + b.city)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`branch-directions-${i}`}
                  className="btn-ghost mt-4"
                >
                  <Navigation size={13} /> {t.common.directions}
                </a>
              </article>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-[#5C4D42] py-10">{c.noBranches}</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
